import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { useState, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { ChartSyncProvider } from "../../components/index.ts";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  useDecisionSummary,
  useDecisionBreakdown,
  type DecisionSummary,
  type DecisionBreakdownBy,
} from "./use-decisions.ts";
import { FailClosedPanel } from "./FailClosedPanel.tsx";
import { TotalsBar } from "./TotalsBar.tsx";
import { AllowDenyChart } from "./AllowDenyChart.tsx";
import { LatencyPanel } from "./LatencyPanel.tsx";
import { BreakdownTable } from "./BreakdownTable.tsx";
import "./decisions.css";

/**
 * The Decisions screen (task 11.6; design 12.2 Decisions row). It composes the regions the design
 * calls for over two REST reads, both role `viewer`: the `GET /api/v1/decisions/summary` snapshot
 * (allow/deny totals, the allow/deny/fail-closed over-time series, decision latency, the distinctly
 * highlighted fail-closed totals, and AIS↔Gateway consistency) and the `GET /api/v1/decisions/
 * breakdown?by=…` snapshot for the user-selected dimension.
 *
 * The over-time widget uses the summary's authoritative contract-defined `series` rather than live
 * stream metrics: the series carries the exact allow/deny/fail-closed buckets the contract defines,
 * so it is the honest source for allow/deny over time (the stream carries no comparably-shaped
 * decision series). Pausing the time-range freezes both queries — the shared window stops advancing
 * and the keys stop changing (design 12.2, FR-13 AC2).
 *
 * The `by` dimension is local screen state; changing it re-queries only the breakdown (its query is
 * keyed on `by`), leaving the summary snapshot in place. The screen's top-level state is driven by
 * the SUMMARY (the primary snapshot); the breakdown is a secondary region that shows its own
 * loading/empty state inside the ready screen so a slow or empty breakdown never blanks the page.
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - restricted: the summary is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / empty / ready as the summary query resolves; a cold-core waking error defers
 *   to the shell's banner (kept "loading" under it so there is no contradiction).
 */
export function DecisionsScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();
  const [by, setBy] = useState<DecisionBreakdownBy>("reason");

  const { query: summary, window } = useDecisionSummary();
  const { query: breakdown } = useDecisionBreakdown(by);

  const state = resolveState(role, summary);

  return (
    <ScreenScaffold
      title="Decisions"
      subtitle="Allow/deny over time, breakdown by reason, tier, operation or gateway, decision latency, and fail-closed events highlighted."
      state={state}
    >
      {summary.data !== undefined && (
        <DecisionsBody
          data={summary.data}
          live={live}
          window={window}
          by={by}
          onByChange={setBy}
          breakdown={breakdown.data}
          breakdownLoading={breakdown.isFetching}
        />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the summary query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useDecisionSummary>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the decisions view is hidden until it is. The server enforces access regardless."
          : "The decisions view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    const d = query.data;
    const allowZero = d.totals.allow.value === 0 || d.totals.allow.value === null;
    const denyZero = d.totals.deny.value === 0 || d.totals.deny.value === null;
    const noSeries = d.series.points.length === 0;
    if (allowZero && denyZero && noSeries) {
      return {
        kind: "empty",
        cause:
          "No boundary decisions were recorded in this window. Widen the time range, or check that AIS and the Gateway are reporting decisions.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useDecisionSummary>["query"]["error"]): string {
  if (error === null) return "The decisions view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see decisions. The server enforces access.";
    case "network":
      return "The decisions request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function DecisionsBody(props: {
  data: DecisionSummary;
  live: boolean;
  window: ReturnType<typeof useDecisionSummary>["window"];
  by: DecisionBreakdownBy;
  onByChange: (by: DecisionBreakdownBy) => void;
  breakdown: ReturnType<typeof useDecisionBreakdown>["query"]["data"];
  breakdownLoading: boolean;
}): ReactNode {
  const { data } = props;
  const generatedAt = data.meta.generated_at;

  return (
    <div className="dec" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="dec__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(props.window.fromMs)}>
          {formatTimeOfDay(props.window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(props.window.toMs)}>
          {formatTimeOfDay(props.window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
      </p>
      </div>

      {/* Fail-closed is surfaced first and prominently (design 12.3). */}
      <FailClosedPanel failClosed={data.totals.fail_closed} />

      <TotalsBar totals={data.totals} consistency={data.consistency} />

      {/* The over-time chart, folded into the observability ChartCard (task 4.1). It keeps its own
          crosshair-sync group; the card reads live/paused from the shared time-range itself. */}
      <ChartSyncProvider>
        <AllowDenyChart series={data.series} />
      </ChartSyncProvider>

      <LatencyPanel latency={data.latency} />

      <BreakdownTable
        by={props.by}
        onByChange={props.onByChange}
        breakdown={props.breakdown}
        loading={props.breakdownLoading}
      />
    </div>
  );
}
